import type { TerminalEvent } from '@developerz/domain';
import {
  createEffect,
  createMemo,
  createSignal,
  type JSX,
  on,
  onCleanup,
  onMount,
  Show,
  untrack,
} from 'solid-js';
import { t } from '../../i18n';
import { isForbidden } from '../../lib/api';
import { computeWindow, countAfter, TERMINAL_ROW_HEIGHT_PX } from '../../lib/terminal/buffer';
import { ANNOUNCE_EVERY_MS, useLiveAnnouncement } from '../../lib/terminal/use-live-announcement';
import type { TerminalTimeMode } from '../../lib/terminal/use-terminal-panes';
import { DeniedState } from '../DeniedState';
import { ErrorState } from '../ErrorState';
import { Button } from '../ui/Button';
import { TerminalRow, type TerminalRowMatch } from './TerminalRow';

// The Terminal window's log body (#4395/#4396, epic #4391): fixed-row-height
// windowing over `props.rows` (chronological, oldest first — the buffer's own
// order), a "Load earlier lines" affordance at the top, follow mode over the
// live tail, and its own loading / error / denied / empty states — never
// `<QueryBoundary/>`'s `.page` wrapper, which this window's `bleed` route
// exists specifically to avoid (Done #2).
//
// FOLLOW MODE (#4396 step 3). On by default: the view pins to the newest row
// as `props.rows` grows, mirroring `tail -f`. Scrolling away from the bottom
// PAUSES it (a real read, not a click) and shows a "N new" pill; the pill or a
// scroll back to the bottom RESUMES it. The pill sits OUTSIDE `.terminal__rows`
// — that element is `role="log"`, and a control inside a live region is an
// avoidable axe finding.
//
// `pinning` guards the one race this design has: pinning to the bottom SETS
// `scrollTop`, which fires a `scroll` event like any other — without the flag,
// that event would run straight back through the "did the user scroll away"
// check on our OWN write, on a burst landing close enough to a rounding edge
// to misread as "not quite at the bottom".
//
// FOLLOW IS CONTROLLED (#4398): the flag lives in the pane layer
// (`use-terminal-panes.ts`'s `followFor`), so the command line's `follow`,
// `pause` and `tail` move the SAME flag this log's scroll and pill do. The
// "N new" marker is set on EVERY true→false edge, whatever caused it — a
// `pause` typed at the prompt counts arrivals exactly like a scroll-away.

const NEAR_BOTTOM_PX = TERMINAL_ROW_HEIGHT_PX;

// THROTTLED ANNOUNCEMENTS (#4401): the log region is `aria-live="off"`; a
// visually hidden status line speaks `useLiveAnnouncement`'s throttled count.

export function TerminalLog(props: {
  /** Oldest first. */
  readonly rows: readonly TerminalEvent[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly hasMore: boolean;
  readonly isFetchingMore: boolean;
  readonly onLoadOlder: () => void;
  readonly onRetry: () => void;
  /** The live tail's own connection state — the trailing cursor (D-J) shows
   *  only while it is genuinely open, and only while following. */
  readonly isLive?: boolean;
  /**
   * Whether `rows` holds any REAL (non-chrome) content — distinct from
   * `rows.length`, which also counts the boot/connection system lines
   * `Terminal.tsx` merges into the same buffer for chronological order. A
   * live session always ends up with at least an "authenticated"/"tailing"
   * line once connected, so `rows.length > 0` alone would call that "No
   * activity yet." wrong the moment the tail opens.
   */
  readonly isEmpty: boolean;
  /**
   * The `role="log"` region's own label. Defaults to the generic
   * `terminal.log_aria_label` — the single-pane shape #4395 shipped. #4397's
   * panes each pass a DISTINCT label (their own filter summary): N identical
   * live regions on one page is an axe finding and a screen reader dead end,
   * not a cosmetic gap.
   */
  readonly ariaLabel?: string;
  /** Whether the view pins to the newest row — owned by the pane layer. */
  readonly follow: boolean;
  readonly onFollowChange: (on: boolean) => void;
  /** #4401 — the window-wide timestamp style. */
  readonly timeMode?: TerminalTimeMode;
  /** #4401 — this pane's search: every matching row id, and the current one. */
  readonly matchIds?: ReadonlySet<string>;
  readonly activeMatchId?: string | null;
  /** How often the throttled live announcement may speak. Tests shorten it. */
  readonly announceEveryMs?: number;
}): JSX.Element {
  let scrollEl: HTMLDivElement | undefined;
  let pinning = false;
  const [scrollTop, setScrollTop] = createSignal(0);
  const [viewportHeight, setViewportHeight] = createSignal(0);
  const follow = (): boolean => props.follow;
  const [pauseMarker, setPauseMarker] = createSignal<{
    readonly id: string;
    readonly ts: string;
  } | null>(null);

  // The marker tracks the follow EDGE, not who flipped it: pausing pins the
  // newest row held right now, resuming drops it.
  createEffect(
    on(follow, (on) => {
      if (on) {
        setPauseMarker(null);
        return;
      }
      const last = untrack(() => props.rows[props.rows.length - 1]);
      setPauseMarker(last === undefined ? null : { id: last.id, ts: last.ts });
    }),
  );

  // Bring the current search match into view (#4401). Jumping to a match is
  // reading history, so follow pauses first: the live tail must not yank the
  // view off the line the reader just asked for.
  createEffect(
    on(
      () => props.activeMatchId,
      (id) => {
        if (id == null || scrollEl === undefined) return;
        const index = untrack(() => props.rows.findIndex((r) => r.id === id));
        if (index === -1) return;
        if (untrack(follow)) props.onFollowChange(false);
        const top = Math.max(0, index * TERMINAL_ROW_HEIGHT_PX - scrollEl.clientHeight / 2);
        scrollEl.scrollTop = top;
        // Our own write fires a scroll event only if it MOVED the view. A jump
        // clamped to the bottom must not read as the reader returning there
        // (that would resume follow and yank them off the match), and a jump
        // that moved nothing must not leave a guard armed to swallow their
        // next real scroll.
        pinning = scrollEl.scrollTop !== untrack(scrollTop);
        setScrollTop(scrollEl.scrollTop);
      },
    ),
  );

  const announcement = useLiveAnnouncement(
    () => props.rows,
    () => props.announceEveryMs ?? ANNOUNCE_EVERY_MS,
  );

  onMount(() => {
    if (scrollEl === undefined) return;
    setViewportHeight(scrollEl.clientHeight);
    const observer = new ResizeObserver(() => {
      if (scrollEl) setViewportHeight(scrollEl.clientHeight);
    });
    observer.observe(scrollEl);
    onCleanup(() => observer.disconnect());
  });

  function scrollToBottom(): void {
    if (scrollEl === undefined) return;
    const el = scrollEl;
    queueMicrotask(() => {
      // Follow may have been dropped between the queue and now — a search jump
      // (#4401) or a typed `pause` in the same tick. A pin that lands after
      // that would undo the jump the reader just asked for, and it must not
      // touch a guard the jump set.
      if (!untrack(follow)) return;
      const before = el.scrollTop;
      el.scrollTop = el.scrollHeight;
      // Guard only a write that moved the view — only that one fires an event.
      if (el.scrollTop !== before) pinning = true;
    });
  }

  // Pin to the newest row on every growth — the backfill's first page (the
  // OLD "scroll to bottom on first load" behaviour) and every live row after
  // it, as long as `follow()` holds.
  createEffect(() => {
    if (props.rows.length === 0) return;
    if (follow()) scrollToBottom();
  });

  function onScroll(e: Event): void {
    const el = e.currentTarget as HTMLDivElement;
    setScrollTop(el.scrollTop);
    if (pinning) {
      pinning = false;
      return;
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom <= NEAR_BOTTOM_PX;
    if (atBottom) {
      if (!follow()) props.onFollowChange(true);
    } else if (follow()) {
      props.onFollowChange(false);
    }
  }

  const newCount = createMemo(() => {
    const marker = pauseMarker();
    return marker === null ? 0 : countAfter(props.rows, marker);
  });

  function resumeFollow(): void {
    props.onFollowChange(true);
    scrollToBottom();
  }

  const win = createMemo(() =>
    computeWindow({
      rowCount: props.rows.length,
      rowHeight: TERMINAL_ROW_HEIGHT_PX,
      viewportHeight: viewportHeight(),
      scrollTop: scrollTop(),
    }),
  );
  const visible = createMemo(() => props.rows.slice(win().start, win().end));

  return (
    // tabindex: a scroll region must be keyboard-reachable (axe scrollable-region-focusable).
    <div class="terminal__body" ref={scrollEl} onScroll={onScroll} tabindex="0">
      <span class="sr-only" role="status">
        {announcement()}
      </span>
      <Show when={props.isError} fallback={<Body {...props} win={win()} visible={visible()} />}>
        <Show
          when={isForbidden(props.error)}
          fallback={<ErrorState error={props.error} onRetry={props.onRetry} />}
        >
          <DeniedState error={props.error} />
        </Show>
      </Show>
      <Show when={!props.isError && props.isLive === true && follow() && props.rows.length > 0}>
        <span class="term__cursor--live" aria-hidden="true" />
      </Show>
      <Show when={!follow() && newCount() > 0}>
        <button type="button" class="terminal__follow-pill" onClick={resumeFollow}>
          <span aria-hidden="true">{t('terminal.follow_new', { count: newCount() })}</span>
          <span class="sr-only">{t('terminal.follow_paused_aria', { count: newCount() })}</span>
        </button>
      </Show>
    </div>
  );
}

function Body(props: {
  readonly rows: readonly TerminalEvent[];
  readonly isLoading: boolean;
  readonly hasMore: boolean;
  readonly isFetchingMore: boolean;
  readonly onLoadOlder: () => void;
  readonly win: ReturnType<typeof computeWindow>;
  readonly visible: readonly TerminalEvent[];
  readonly isEmpty: boolean;
  readonly ariaLabel?: string;
  readonly timeMode?: TerminalTimeMode | undefined;
  readonly matchIds?: ReadonlySet<string> | undefined;
  readonly activeMatchId?: string | null | undefined;
}): JSX.Element {
  const matchOf = (id: string): TerminalRowMatch =>
    id === props.activeMatchId ? 'active' : props.matchIds?.has(id) === true ? 'match' : null;
  return (
    <Show
      when={!props.isLoading}
      fallback={
        <div role="status">
          <span class="sr-only">{t('states.loading')}</span>
        </div>
      }
    >
      <Show when={!props.isEmpty} fallback={<p class="terminal__empty">{t('terminal.empty')}</p>}>
        <Show when={props.hasMore}>
          <div class="terminal__load-older">
            <Button
              variant="outline"
              size="sm"
              disabled={props.isFetchingMore}
              onClick={props.onLoadOlder}
            >
              {t('terminal.load_older')}
            </Button>
          </div>
        </Show>
        <div
          class="terminal__rows"
          role="log"
          aria-live="off"
          aria-label={props.ariaLabel ?? t('terminal.log_aria_label')}
          style={{ height: `${props.win.totalHeight}px`, position: 'relative' }}
        >
          <div style={{ transform: `translateY(${props.win.topOffset}px)` }}>
            {props.visible.map((event) => (
              <TerminalRow event={event} timeMode={props.timeMode} match={matchOf(event.id)} />
            ))}
          </div>
        </div>
      </Show>
    </Show>
  );
}
